import {
  Avatar,
  Box,
  Button,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import StandardDialog from "../../components/StandardDialog.js";
import headshot from "../../assets/headshot.png";
import { getLocalStartTime, useCampaignPhase } from "./campaign.js";

// Renders as a speech bubble "from Will": a pre-campaign variant that tells people what's
// coming, and a live variant with the actual ask. Both read the phase themselves, so
// callers only need to manage `open`.
//
// The shell (avatar, header, bubble, CTA row) is worth keeping between campaigns; the
// copy and the CTA below are meant to be overwritten each time.
const CampaignDialog = ({ open, onClose }) => {
  const { phase, daysUntil } = useCampaignPhase();
  const isLive = phase === "live";

  let title;
  if (isLive) {
    title = "Something's happening at Curate today 🎉";
  } else if (daysUntil === 0) {
    title = "Something's happening at Curate later today 🎉";
  } else if (daysUntil === 1) {
    title = "Something's happening at Curate tomorrow 🎉";
  } else {
    title = `Something's happening at Curate in ${daysUntil} days 🎉`;
  }

  return (
    <StandardDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent sx={{ pt: 3, position: "relative" }}>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{ position: "absolute", top: 8, right: 8 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar
            src={headshot}
            alt="Will, founder of Curate"
            sx={{ width: 44, height: 44 }}
          />
          <Box>
            <Typography variant="subtitle2" sx={{ lineHeight: 1 }}>
              <b>Will</b>
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ lineHeight: 1, mt: 0.25, display: "block" }}
            >
              Founder of Curate
            </Typography>
          </Box>
        </Stack>

        <Box
          sx={{
            position: "relative",
            mt: 1.5,
            p: 2,
            borderRadius: 2,
            bgcolor: "secondary.main",
            color: "secondary.contrastText",
            "&::before": {
              content: '""',
              position: "absolute",
              top: -9,
              left: 16,
              borderLeft: "9px solid transparent",
              borderRight: "9px solid transparent",
              borderBottom: "10px solid",
              borderBottomColor: "secondary.main",
            },
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 700 }} gutterBottom>
            {title}
          </Typography>

          {/* Campaign copy — overwrite both variants for each campaign. Links inside the
          bubble should use color="inherit" with underline="always": the theme's link
          green fails contrast against the light-mode bubble, so the underline has to
          carry the affordance. */}
          {isLive ? (
            <Typography variant="body2">
              Placeholder copy for the live phase — what's happening, and what
              you'd like people to do about it.
            </Typography>
          ) : (
            <Typography variant="body2">
              Placeholder copy for the run-up — what's coming and when. Use{" "}
              {getLocalStartTime()} to state the start in the reader's own
              timezone.
            </Typography>
          )}
        </Box>

        <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
          <Button variant="contained" onClick={onClose}>
            Got it
          </Button>
        </Box>
      </DialogContent>
    </StandardDialog>
  );
};

export default CampaignDialog;
